import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, ArrowRight, Sparkles, ShieldCheck, Activity, LayoutDashboard } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import LanguageToggle from '../components/LanguageToggle';
import TamkeenLogo from '../components/TamkeenLogo';
import promoVideo from '../assets/tamkeen-promo.mp4';

// 1. استيراد سياق المصادقة لإصلاح عمى الحالة
import { useAuth } from '../context/AuthContext';

const WelcomePage = () => {
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const { user } = useAuth(); // قراءة الهوية الحالية إن وجدت

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.2, delayChildren: 0.1 } }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { y: 0, opacity: 1, transition: { type: "spring", stiffness: 100 } }
  };

  // 2. دالة التوجيه الذكي (Smart Routing) بناءً على معمارية الصلاحيات
  const handleCtaClick = () => {
    if (user) {
      if (user.role === 'admin') {
        navigate('/admin');
      } else {
        navigate('/workshop');
      }
    } else {
      navigate('/login');
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center relative overflow-hidden bg-slate-50 font-sans pb-20">
      
      <LanguageToggle />

      <div className="absolute top-[-10%] start-[-10%] w-96 h-96 bg-blue-400 rounded-full mix-blend-multiply filter blur-[128px] opacity-40"></div>
      <div className="absolute bottom-[-10%] end-[-10%] w-96 h-96 bg-emerald-400 rounded-full mix-blend-multiply filter blur-[128px] opacity-40"></div>

      <motion.div variants={containerVariants} initial="hidden" animate="visible" className="z-10 w-full max-w-5xl px-6 flex flex-col items-center text-center mt-24">
        
        <motion.div variants={itemVariants} className="mb-6 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/50 border border-white/80 shadow-sm backdrop-blur-md">
          <Sparkles className="w-4 h-4 text-blue-500" />
          <span className="text-sm font-bold text-slate-700">{t('welcome.badge')}</span>
        </motion.div>

        <motion.div variants={itemVariants} className="mb-4">
          <TamkeenLogo className="w-20 h-20 drop-shadow-lg" />
        </motion.div>

        <motion.h1 variants={itemVariants} className="text-5xl md:text-7xl font-extrabold text-slate-900 tracking-tight mb-6">
          {t('welcome.title')} <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-emerald-500">{t('welcome.titleHighlight')}</span>
        </motion.h1>        
        
        <motion.p variants={itemVariants} className="text-lg md:text-xl text-slate-600 max-w-2xl mb-12 leading-relaxed">
          {t('welcome.subtitle')}
        </motion.p>

        <motion.div variants={itemVariants} className="flex flex-col sm:flex-row gap-4 mb-20 w-full sm:w-auto">
          <button 
            onClick={handleCtaClick} 
            className="group relative px-8 py-4 bg-slate-900 text-white rounded-2xl font-bold text-lg shadow-xl shadow-slate-900/20 hover:-translate-y-1 transition-all duration-300 flex items-center justify-center gap-3"
          >
            <span className="relative z-10 flex items-center gap-2">
              {/* 3. تغيير النص برمجياً لتجنب التناقض المعماري */}
              {user ? (
                <>
                  <LayoutDashboard className="w-5 h-5" />
                  {user.role === 'admin' ? 'العودة للإدارة المركزية' : 'الدخول لمساحة المربي'}
                </>
              ) : (
                <>
                  {t('welcome.cta')} 
                  {language === 'ar' ? (
                    <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
                  ) : (
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  )}
                </>
              )}
            </span>
          </button>
        </motion.div>

        <motion.div variants={containerVariants} className="grid grid-cols-1 md:grid-cols-3 gap-6 w-full mb-24">
          <FeatureCard 
            icon={<Activity className="w-8 h-8 text-blue-500" />}
            title={t('welcome.feature1Title')}
            description={t('welcome.feature1Desc')}
          />
          <FeatureCard 
            icon={<ShieldCheck className="w-8 h-8 text-emerald-500" />}
            title={t('welcome.feature2Title')}
            description={t('welcome.feature2Desc')}
          />
          <FeatureCard 
            icon={<svg className="w-8 h-8 text-purple-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 10l-2 1m0 0l-2-1m2 1v2.5M20 7l-2 1m2-1l-2-1m2 1v2.5M14 4l-2-1-2 1M4 7l2-1M4 7l2 1M4 7v2.5M12 21l-2-1m2 1l2-1m-2 1v-2.5M6 18l-2-1v-2.5M18 18l2-1v-2.5" /></svg>}
            title={t('welcome.feature3Title')}
            description={t('welcome.feature3Desc')}
          />
        </motion.div>

        <motion.div variants={itemVariants} className="w-full max-w-4xl flex flex-col items-center">
          <h2 className="text-2xl font-bold text-slate-800 mb-8">{t('welcome.videoTitle')}</h2>
          <div className="w-full aspect-video rounded-[2rem] overflow-hidden shadow-2xl border-8 border-white bg-slate-900 relative flex items-center justify-center">
            {/* إغلاق ثغرة الأداء بإيقاف التحميل التلقائي وحقن الفيديو المحلي */}
            <video 
              className="w-full h-full object-cover"
              controls 
              preload="metadata"
              src={promoVideo} 
            >
              متصفحك لا يدعم مشغل الفيديو.
            </video>
          </div>
        </motion.div>

      </motion.div>
    </div>
  );
};

const FeatureCard = ({ icon, title, description }) => (
  <motion.div 
    variants={{ hidden: { y: 20, opacity: 0 }, visible: { y: 0, opacity: 1, transition: { type: "spring", stiffness: 100 } } }}
    whileHover={{ y: -5, scale: 1.02 }}
    className="bg-white/60 backdrop-blur-xl border border-white/80 p-8 rounded-3xl shadow-lg flex flex-col items-center text-center transition-all duration-300"
  >
    <div className="w-16 h-16 rounded-2xl bg-white shadow-sm flex items-center justify-center mb-6 border border-slate-100">
      {icon}
    </div>
    <h3 className="text-xl font-bold text-slate-800 mb-3">{title}</h3>
    <p className="text-slate-500 leading-relaxed text-sm">{description}</p>
  </motion.div>
);

export default WelcomePage;